define(["require", "exports", "tslib", "@arcgis/core/geometry/Polygon", "@arcgis/core/geometry/Polyline", "@arcgis/core/geometry/geometryEngine", "@arcgis/core/rest/geometryService", "@arcgis/core/rest/support/AreasAndLengthsParameters", "@arcgis/core/rest/support/BufferParameters", "@arcgis/core/rest/support/LengthsParameters", "./SpatialReferenceFactory"], function (require, exports, tslib_1, Polygon_1, Polyline_1, GeometryEngine, geometryService, AreasAndLengthsParameters_1, BufferParameters_1, LengthsParameters_1, SpatialReferenceFactory_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.GeometryUtil = void 0;
    Polygon_1 = tslib_1.__importDefault(Polygon_1);
    Polyline_1 = tslib_1.__importDefault(Polyline_1);
    GeometryEngine = tslib_1.__importStar(GeometryEngine);
    geometryService = tslib_1.__importStar(geometryService);
    AreasAndLengthsParameters_1 = tslib_1.__importDefault(AreasAndLengthsParameters_1);
    BufferParameters_1 = tslib_1.__importDefault(BufferParameters_1);
    LengthsParameters_1 = tslib_1.__importDefault(LengthsParameters_1);
    const EARTH_CIRCUM = 2 * 3.14159265 * 6378137;
    class GeometryUtil {
        static getMaxAllowableOffset(map) {
            return (EARTH_CIRCUM / (256 * Math.pow(2, map.zoom))) / (EARTH_CIRCUM / 360);
        }
        static union(geometryServiceUrl, geometries) {
            return new Promise((resolve, reject) => {
                if (geometries.length > 0) {
                    if (geometries.length > 1) {
                        geometryService.union(geometryServiceUrl, geometries)
                            .then(geometry => {
                            if (geometry) {
                                resolve(geometry);
                            }
                            else {
                                reject(new Error("Geometry Service failed to union geometries"));
                            }
                        }).catch(reject);
                    }
                    else {
                        resolve(geometries[0]);
                    }
                }
                else {
                    reject(new Error("Unable to union empty geometry collection"));
                }
            });
        }
        static buffer(geometryServiceUrl, geometry, distance, unit) {
            const params = new BufferParameters_1.default();
            params.geometries = geometry;
            params.distances = distance;
            params.unit = unit == "m" ? "meters" : "kilometers";
            params.bufferSpatialReference = SpatialReferenceFactory_1.SpatialReferenceFactory.findOptimalProjectionFromGeographicCoordSystem(GeometryUtil.getCentroid(geometry[0]));
            params.outSpatialReference = SpatialReferenceFactory_1.SpatialReferenceFactory.GDA_1994;
            return new Promise((resolve, reject) => {
                geometryService.buffer(geometryServiceUrl, params).then(resolve).catch(reject);
            });
        }
        static generalize(x, maxDeviation, removeDegenerateParts, maxDeviationUnit) {
            return GeometryEngine.generalize(x, maxDeviation, removeDegenerateParts, maxDeviationUnit);
        }
        static generalizePolylinePreservePartEndpoints(polyline, maxDeviation, removeDegenerateParts, maxDeviationUnit) {
            const original = polyline.clone();
            const generalized = GeometryEngine.generalize(polyline, maxDeviation, removeDegenerateParts, maxDeviationUnit);
            if (!(generalized instanceof Polyline_1.default)) {
                return original;
            }
            const originalPaths = original.paths.filter(path => path && path.length > 0);
            const generalizedPaths = generalized.paths.filter(path => path && path.length > 0);
            if (originalPaths.length !== generalizedPaths.length) {
                return original;
            }
            const resultPaths = generalizedPaths.map(path => path.map(vertex => [...vertex]));
            for (let i = 0; i < resultPaths.length; i++) {
                const sourcePath = originalPaths[i];
                const targetPath = resultPaths[i];
                if (!sourcePath || sourcePath.length === 0 || !targetPath || targetPath.length === 0) {
                    return original;
                }
                const startVertex = [...sourcePath[0]];
                const endVertex = [...sourcePath[sourcePath.length - 1]];
                targetPath[0] = startVertex;
                if (targetPath.length === 1) {
                    const sameStartEnd = startVertex.length >= 2 &&
                        endVertex.length >= 2 &&
                        startVertex[0] === endVertex[0] &&
                        startVertex[1] === endVertex[1];
                    if (!sameStartEnd) {
                        targetPath.push(endVertex);
                    }
                }
                else {
                    targetPath[targetPath.length - 1] = endVertex;
                }
            }
            return new Polyline_1.default({
                spatialReference: generalized.spatialReference || original.spatialReference,
                paths: resultPaths
            });
        }
        static simplify(x) {
            return GeometryEngine.simplify(x);
        }
        static disjoint(a, b) {
            return GeometryEngine.disjoint(a, b);
        }
        static distance(a, b, unit = null) {
            return GeometryEngine.distance(a, b, unit);
        }
        static closestPolylinePoint(a, b, unit = null) {
            let shortestDistance = 1000000;
            let closest = null;
            for (let o = 0; o < a.paths.length; o++) {
                for (let i = 0; i < a.paths[o].length; i++) {
                    const point = a.getPoint(o, i);
                    const distance = GeometryEngine.distance(point, b, unit);
                    if (distance < shortestDistance) {
                        shortestDistance = distance;
                        closest = point;
                    }
                }
            }
            return { point: closest, distance: shortestDistance };
        }
        static areEqual(a, b) {
            return GeometryEngine.equals(a, b);
        }
        static getCentroid(x) {
            return x instanceof Polygon_1.default || x instanceof Polyline_1.default ? x.extent.center : x;
        }
        static getPolylineStartPoint(polyline) {
            return polyline.getPoint(0, 0);
        }
        static setPolylineStartPoint(polyline, point) {
            polyline.setPoint(0, 0, point);
        }
        static getPolylineEndPoint(polyline) {
            const lastPathIndex = polyline.paths.length - 1;
            const lastPathLastPointIndex = polyline.paths[lastPathIndex].length - 1;
            return polyline.getPoint(lastPathIndex, lastPathLastPointIndex);
        }
        static setPolylineEndPoint(polyline, point) {
            const lastPathIndex = polyline.paths.length - 1;
            const lastPathLastPointIndex = polyline.paths[lastPathIndex].length - 1;
            polyline.setPoint(lastPathIndex, lastPathLastPointIndex, point);
        }
        static measureLength(geometryServiceUrl, g, unit = "meters") {
            return new Promise((resolve, reject) => {
                if (g) {
                    let p = new LengthsParameters_1.default();
                    p.polylines = [g];
                    p.lengthUnit = unit == "meters" ? 9001 : 9036;
                    p.geodesic = true;
                    geometryService.lengths(geometryServiceUrl, p)
                        .then(res => {
                        if (res && res.lengths.length > 0) {
                            resolve(res.lengths[0]);
                        }
                        else {
                            reject(new Error("measureLength failed"));
                        }
                    }).catch(reject);
                }
                else {
                    reject(new Error("Invalid input"));
                }
            });
        }
        static measureArea(geometryServiceUrl, g, areaUnit = "acres", lengthUnit = "meters") {
            return new Promise((resolve, reject) => {
                if (g) {
                    let q = new AreasAndLengthsParameters_1.default();
                    q.polygons = [g];
                    q.lengthUnit = lengthUnit;
                    q.areaUnit = areaUnit;
                    geometryService.areasAndLengths(geometryServiceUrl, q)
                        .then((res) => {
                        if (res) {
                            resolve({ area: res.areas[0], length: res.lengths[0] });
                        }
                        else {
                            reject(new Error("measureArea failed"));
                        }
                    }).catch(reject);
                }
                else {
                    reject(new Error("Invalid input"));
                }
            });
        }
        static mergePolylineToSinglePath(polyline, joinTolerance = 1e-12) {
            const originalPaths = polyline.paths.filter(path => path && path.length > 0);
            if (originalPaths.length <= 1) {
                return polyline.clone();
            }
            const distanceSq = (a, b) => {
                const dx = a[0] - b[0];
                const dy = a[1] - b[1];
                return dx * dx + dy * dy;
            };
            const joinToleranceSq = joinTolerance * joinTolerance;
            const isWithinJoinTolerance = (a, b) => distanceSq(a, b) <= joinToleranceSq;
            let mergedPath = [...originalPaths[0]];
            for (let i = 1; i < originalPaths.length; i++) {
                const nextPath = originalPaths[i];
                const lastMergedPoint = mergedPath[mergedPath.length - 1];
                const firstNextPoint = nextPath[0];
                if (isWithinJoinTolerance(lastMergedPoint, firstNextPoint)) {
                    mergedPath = mergedPath.concat(nextPath.slice(1));
                }
                else {
                    mergedPath = mergedPath.concat(nextPath);
                }
            }
            return new Polyline_1.default({
                spatialReference: polyline.spatialReference,
                paths: [mergedPath]
            });
        }
        static mergePolylineToSinglePathPreserveAllVertices(polyline) {
            const originalPaths = polyline.paths.filter(path => path && path.length > 0);
            if (originalPaths.length <= 1) {
                return polyline.clone();
            }
            let mergedPath = [];
            for (let i = 0; i < originalPaths.length; i++) {
                mergedPath = mergedPath.concat(originalPaths[i]);
            }
            return new Polyline_1.default({
                spatialReference: polyline.spatialReference,
                paths: [mergedPath]
            });
        }
    }
    exports.GeometryUtil = GeometryUtil;
});
